import { useRef, useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Upload, X, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { uploadFile as erpUploadFile } from '@/lib/erp/files';

interface FileUploadFieldProps {
  label: string;
  value?: string;
  onChange: (fileUrl: string) => void;
  allowedFileTypes?: string;
  maxFileSize?: number; // MB
  required?: boolean;
  disabled?: boolean;
  error?: string;
}

export function FileUploadField({
  label,
  value,
  onChange,
  allowedFileTypes,
  maxFileSize = 5,
  required = false,
  disabled = false,
  error: externalError,
}: FileUploadFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const error = externalError || uploadError;
  const hasFile = !!value;

  const acceptText = allowedFileTypes
    ? allowedFileTypes.split(',').map((t) => t.trim().toUpperCase()).join(', ')
    : 'PNG, JPG, PDF';

  const acceptAttr = allowedFileTypes
    ? allowedFileTypes.split(',').map((t) => `.${t.trim().toLowerCase()}`).join(',')
    : 'image/*,.pdf';

  const uploadFile = useCallback(async (file: File) => {
    if (disabled || isUploading) return;
    setUploadError(null);

    // Validate file size
    if (file.size > maxFileSize * 1024 * 1024) {
      setUploadError(`File exceeds ${maxFileSize}MB limit`);
      return;
    }

    // Validate file type
    if (allowedFileTypes) {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      const allowed = allowedFileTypes.split(',').map((t) => t.trim().toLowerCase());
      if (!allowed.includes(ext)) {
        setUploadError(`File type .${ext} not allowed. Use: ${acceptText}`);
        return;
      }
    }

    setIsUploading(true);
    try {
      const result = await erpUploadFile(file, { isPrivate: false, folder: 'Home' });
      onChange(result.file_url);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  }, [disabled, isUploading, maxFileSize, allowedFileTypes, acceptText, onChange]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) uploadFile(file);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [uploadFile],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isUploading) setIsDragging(true);
  }, [disabled, isUploading]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  }, [uploadFile]);

  const handleRemove = useCallback(() => {
    onChange('');
    setUploadError(null);
  }, [onChange]);

  const fileName = value ? decodeURIComponent(value.split('/').pop() || 'file') : '';
  const isImage = value ? /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(value) : false;

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-foreground">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>

      <input
        ref={fileInputRef}
        type="file"
        accept={acceptAttr}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled}
      />

      {/* Uploaded state */}
      {hasFile && !isUploading && (
        <div className="flex items-center gap-3 rounded-lg border bg-card p-3">
          <div className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted">
            {isImage ? (
              <img src={value} alt={fileName} className="size-14 rounded-md object-cover" />
            ) : (
              <FileText className="size-6 text-muted-foreground" />
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-sm font-medium text-foreground">{fileName}</span>
            {!disabled && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-fit h-auto p-0 text-xs text-red-500 hover:text-red-700"
                onClick={handleRemove}
              >
                <X className="h-3 w-3 mr-1" />
                Remove
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Uploading state */}
      {isUploading && (
        <div className="flex min-h-[80px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-sky-300 bg-sky-50 dark:bg-sky-950/30 p-4">
          <Loader2 className="size-6 animate-spin text-sky-500" />
          <p className="text-sm text-sky-600 dark:text-sky-400">Uploading...</p>
        </div>
      )}

      {/* Drop zone */}
      {!hasFile && !isUploading && (
        <button
          type="button"
          onClick={() => !disabled && fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          disabled={disabled}
          className={cn(
            'flex min-h-[80px] flex-col items-center justify-center gap-2',
            'rounded-lg border-2 border-dashed p-4 transition-colors duration-150',
            disabled
              ? 'cursor-not-allowed border-border bg-muted opacity-60'
              : isDragging
                ? 'border-solid border-sky-400 bg-sky-50 dark:bg-sky-950/30'
                : 'cursor-pointer border-border bg-muted/30 hover:border-muted-foreground/40 hover:bg-muted/50',
            error && !isDragging && 'border-red-400',
          )}
        >
          <Upload className="size-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {isDragging ? 'Drop file here' : 'Drop file or tap to browse'}
          </p>
          <p className="text-xs text-muted-foreground/70">
            {acceptText} up to {maxFileSize}MB
          </p>
        </button>
      )}

      {error && <p className="text-xs text-red-500" role="alert">{error}</p>}
    </div>
  );
}
